import React from 'react';

type ThreeDEffectProps = {
  children: React.ReactNode;
  className?: string;
  baseScale?: number;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  transformOrigin?: string;
  activeOverride?: boolean;
};

// Shared "3d effect": Apple TV-style tilt with a moving glass highlight.
export default function ThreeDEffect({ children, className, baseScale = 1, onClick, transformOrigin = 'center center', activeOverride }: ThreeDEffectProps) {
  const ref = React.useRef<HTMLDivElement>(null);
  const glowRef = React.useRef<HTMLDivElement>(null);
  const rafRef = React.useRef<number | null>(null);
  const rotateXRef = React.useRef(0);
  const rotateYRef = React.useRef(0);
  const glowXRef = React.useRef(50);
  const glowYRef = React.useRef(50);
  const hoveredRef = React.useRef(false);
  const pressedRef = React.useRef(false);
  const [supportsFineHover, setSupportsFineHover] = React.useState(true);
  const settleTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverScale = Math.max(baseScale, 1.04);
  const touchScale = Math.max(baseScale, 1.02);
  const interactiveScale = supportsFineHover ? hoverScale : touchScale;

  const applyVisualState = React.useCallback((options?: { easing?: 'fast' | 'settle' }) => {
    const element = ref.current;
    const glowElement = glowRef.current;
    if (!element) return;

    const active = typeof activeOverride === 'boolean' ? activeOverride : (hoveredRef.current || pressedRef.current);
    const scale = active ? interactiveScale : baseScale;
    const easing = options?.easing === 'fast'
      ? 'transform 120ms cubic-bezier(0.2, 0.8, 0.2, 1)'
      : 'transform 320ms cubic-bezier(0.22, 1, 0.36, 1)';

    element.style.transition = easing;
    element.style.transform = `perspective(600px) rotateX(${supportsFineHover ? rotateXRef.current : 0}deg) rotateY(${supportsFineHover ? rotateYRef.current : 0}deg) scale3d(${scale},${scale},${scale})`;

    if (glowElement) {
      glowElement.style.background = `radial-gradient(circle at ${glowXRef.current}% ${glowYRef.current}%, rgba(255,255,255,${supportsFineHover ? '0.18' : '0.13'}) 0%, transparent 65%)`;
      glowElement.style.opacity = active ? '1' : '0';
      glowElement.style.transition = active ? 'opacity 180ms ease-out' : 'opacity 320ms ease-out';
    }
  }, [activeOverride, baseScale, interactiveScale, supportsFineHover]);

  const queueVisualUpdate = React.useCallback((options?: { easing?: 'fast' | 'settle' }) => {
    if (rafRef.current !== null) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      applyVisualState(options);
    });
  }, [applyVisualState]);

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    const update = () => setSupportsFineHover(mediaQuery.matches);
    update();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', update);
      return () => mediaQuery.removeEventListener('change', update);
    }

    mediaQuery.addListener(update);
    return () => mediaQuery.removeListener(update);
  }, []);

  const scheduleSettle = React.useCallback(() => {
    if (settleTimeoutRef.current) {
      clearTimeout(settleTimeoutRef.current);
    }

    settleTimeoutRef.current = setTimeout(() => {
      rotateXRef.current = 0;
      rotateYRef.current = 0;
      queueVisualUpdate({ easing: 'settle' });
    }, 200);
  }, [queueVisualUpdate]);

  const resetInteractionState = React.useCallback((options?: { immediate?: boolean }) => {
    if (settleTimeoutRef.current) {
      clearTimeout(settleTimeoutRef.current);
      settleTimeoutRef.current = null;
    }

    if (rafRef.current !== null) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    hoveredRef.current = false;
    pressedRef.current = false;
    rotateXRef.current = 0;
    rotateYRef.current = 0;
    glowXRef.current = 50;
    glowYRef.current = 50;

    if (options?.immediate) {
      applyVisualState({ easing: 'settle' });
      return;
    }

    queueVisualUpdate({ easing: 'settle' });
  }, [applyVisualState, queueVisualUpdate]);

  const onPointerEnter = () => {
    hoveredRef.current = true;
    queueVisualUpdate({ easing: 'fast' });
  };

  const onMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!supportsFineHover) return;

    const element = ref.current;
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    const rotateY = (x - 0.5) * 10;
    const rotateX = -(y - 0.5) * 8;

    rotateXRef.current = rotateX;
    rotateYRef.current = rotateY;
    glowXRef.current = x * 100;
    glowYRef.current = y * 100;
    queueVisualUpdate({ easing: 'fast' });
    scheduleSettle();
  };

  const onPointerLeave = () => {
    resetInteractionState({ immediate: true });
  };

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (supportsFineHover || event.pointerType === 'mouse') return;

    pressedRef.current = true;
    const element = ref.current;
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    glowXRef.current = x;
    glowYRef.current = y;
    queueVisualUpdate({ easing: 'fast' });
  };

  const onPointerUp = () => {
    pressedRef.current = false;
    queueVisualUpdate({ easing: 'settle' });
  };

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleWindowPointerMove = (event: PointerEvent) => {
      if (!hoveredRef.current && !pressedRef.current) return;

      const element = ref.current;
      if (!element) return;

      const rect = element.getBoundingClientRect();
      const insideX = event.clientX >= rect.left && event.clientX <= rect.right;
      const insideY = event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (insideX && insideY) return;

      resetInteractionState({ immediate: true });
    };

    const handleWindowBlur = () => {
      resetInteractionState({ immediate: true });
    };

    window.addEventListener('pointermove', handleWindowPointerMove, { passive: true });
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      window.removeEventListener('pointermove', handleWindowPointerMove);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [resetInteractionState]);

  React.useEffect(() => {
    if (typeof activeOverride !== 'boolean') return;

    if (!activeOverride) {
      hoveredRef.current = false;
      pressedRef.current = false;
      rotateXRef.current = 0;
      rotateYRef.current = 0;
      glowXRef.current = 50;
      glowYRef.current = 50;
      queueVisualUpdate({ easing: 'settle' });
      return;
    }

    hoveredRef.current = true;
    queueVisualUpdate({ easing: 'fast' });
  }, [activeOverride, queueVisualUpdate]);

  React.useEffect(() => {
    applyVisualState({ easing: 'settle' });
  }, [applyVisualState]);

  React.useEffect(() => {
    return () => {
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
      }
      if (settleTimeoutRef.current) {
        clearTimeout(settleTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={ref}
      onClick={onClick}
      onPointerEnter={onPointerEnter}
      onMouseMove={onMouseMove}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onPointerLeave={onPointerLeave}
      onMouseLeave={onPointerLeave}
      onBlur={onPointerLeave}
      className={className}
      style={{
        transform: `perspective(600px) rotateX(0deg) rotateY(0deg) scale3d(${baseScale},${baseScale},${baseScale})`,
        transformOrigin,
        transition: 'transform 320ms cubic-bezier(0.22, 1, 0.36, 1)',
        willChange: 'transform',
      }}
    >
      {children}
      <div
        ref={glowRef}
        className="pointer-events-none absolute inset-0 rounded-[inherit]"
        style={{
          background: `radial-gradient(circle at 50% 50%, rgba(255,255,255,${supportsFineHover ? '0.18' : '0.13'}) 0%, transparent 65%)`,
          opacity: 0,
          transition: 'opacity 320ms ease-out',
        }}
      />
    </div>
  );
}